// The top of the app's information card for an event or a club, as the app draws it
// (event-radar-frontend: screens/map/club-card/ClubInfoCard.js and EventInfoCard.js), in a
// phone on Schick blue: a picture to drop into an organiser's DM when asking whether
// Schick may show their picture. Sizes are the app's points, drawn at SCALE.

const SCREEN_PT = 393; // iPhone 15/16 width in points
const IG_ASPECT = 5 / 4; // h/w: taller pictures are cut to 4:5, top and bottom alike
const HERO_BUMP_UP = 14;
const INSET_TOP = 59; // safe area above the content on a phone with a Dynamic Island
const CARD_OVERLAP = 12;
const TAB_BAR_PT = 61;

const CANVAS_WIDTH = 1080;
const PHONE_WIDTH = 720;
const PHONE_BEZEL = 18;
const PHONE_TOP = 170;
const SCREEN_WIDTH = PHONE_WIDTH - 2 * PHONE_BEZEL;
const SCALE = SCREEN_WIDTH / SCREEN_PT;
const BELOW_TABS = 56; // px of the card still shown under the tab bar, where the phone is cut

const BLUE = '#006cfd';
const FONT = '-apple-system, BlinkMacSystemFont, "SF Pro Text", "Segoe UI", Roboto, Helvetica, Arial, sans-serif';
const FOLLOW_GRADIENT = ['#F13BA8', '#6847D8', '#087FD1'];

/** The address as the card's pill shows it: without ", 1020 Wien". */
export const locationLabel = (address = '') => String(address || '')
  .replace(/\s*,?\s*\d{4}\s*,?\s*Wien\s*$/i, '')
  .trim();

/** How the card frames a picture of this size: the box's height per width, and whether it is cut. */
export const heroBox = (width, height) => {
  const ratio = width > 0 && height > 0 ? Math.max(0.5, Math.min(2.5, height / width)) : IG_ASPECT;
  const tall = ratio > IG_ASPECT;
  return { boxAspect: tall ? IG_ASPECT : ratio, tall };
};

/** The canvas height that shows the card down to just under its tabs. */
export const previewHeight = (boxAspect) =>
  Math.round(PHONE_TOP + PHONE_BEZEL + SCREEN_WIDTH * boxAspect + (TAB_BAR_PT - CARD_OVERLAP) * SCALE + BELOW_TABS);

const pt = (value) => value * SCALE;

function roundRect(ctx, x, y, w, h, r) {
  const radius = typeof r === 'number' ? { tl: r, tr: r, br: r, bl: r } : r;
  ctx.beginPath();
  ctx.moveTo(x + radius.tl, y);
  ctx.lineTo(x + w - radius.tr, y);
  ctx.quadraticCurveTo(x + w, y, x + w, y + radius.tr);
  ctx.lineTo(x + w, y + h - radius.br);
  ctx.quadraticCurveTo(x + w, y + h, x + w - radius.br, y + h);
  ctx.lineTo(x + radius.bl, y + h);
  ctx.quadraticCurveTo(x, y + h, x, y + h - radius.bl);
  ctx.lineTo(x, y + radius.tl);
  ctx.quadraticCurveTo(x, y, x + radius.tl, y);
  ctx.closePath();
}

/** Cuts {@code text} with "…" until it fits {@code maxWidth} in the current font. */
function ellipsize(ctx, text, maxWidth) {
  if (ctx.measureText(text).width <= maxWidth) return text;
  let cut = text;
  while (cut.length > 1 && ctx.measureText(`${cut}…`).width > maxWidth) cut = cut.slice(0, -1);
  return `${cut.trimEnd()}…`;
}

function drawBackground(ctx, height) {
  ctx.fillStyle = BLUE;
  ctx.fillRect(0, 0, CANVAS_WIDTH, height);
  const glow = ctx.createRadialGradient(CANVAS_WIDTH * 0.2, 0, 40, CANVAS_WIDTH * 0.2, 0, CANVAS_WIDTH * 0.9);
  glow.addColorStop(0, 'rgba(255,255,255,0.22)');
  glow.addColorStop(1, 'rgba(255,255,255,0)');
  ctx.fillStyle = glow;
  ctx.fillRect(0, 0, CANVAS_WIDTH, height);
  const shade = ctx.createLinearGradient(0, height * 0.55, 0, height);
  shade.addColorStop(0, 'rgba(0,30,90,0)');
  shade.addColorStop(1, 'rgba(0,30,90,0.28)');
  ctx.fillStyle = shade;
  ctx.fillRect(0, 0, CANVAS_WIDTH, height);
}

function drawLogo(ctx, logo) {
  if (!logo) return;
  const width = 116;
  const height = width * ((logo.height || 1) / (logo.width || 1));
  ctx.drawImage(logo, (CANVAS_WIDTH - width) / 2, (PHONE_TOP - height) / 2, width, height);
}

function drawPhoneBody(ctx, height) {
  const x = (CANVAS_WIDTH - PHONE_WIDTH) / 2;
  const bodyHeight = height - PHONE_TOP + 200; // runs off the bottom edge
  ctx.save();
  ctx.shadowColor = 'rgba(0, 20, 60, 0.45)';
  ctx.shadowBlur = 70;
  ctx.shadowOffsetY = 30;
  roundRect(ctx, x, PHONE_TOP, PHONE_WIDTH, bodyHeight, 118);
  const metal = ctx.createLinearGradient(x, 0, x + PHONE_WIDTH, 0);
  metal.addColorStop(0, '#2c2d33');
  metal.addColorStop(0.5, '#17181c');
  metal.addColorStop(1, '#2c2d33');
  ctx.fillStyle = metal;
  ctx.fill();
  ctx.restore();
  roundRect(ctx, x + 4, PHONE_TOP + 4, PHONE_WIDTH - 8, bodyHeight, 114);
  ctx.strokeStyle = 'rgba(255,255,255,0.10)';
  ctx.lineWidth = 2;
  ctx.stroke();
}

function drawPicture(ctx, image, left, top, width, boxHeight, tall) {
  ctx.fillStyle = '#000';
  ctx.fillRect(left, top, width, boxHeight);
  const iw = image.width;
  const ih = image.height;
  if (tall) {
    // cover: as wide as the screen, the middle 4:5 of it
    const drawH = width * (ih / iw);
    ctx.drawImage(image, left, top - (drawH - boxHeight) / 2, width, drawH);
  } else {
    // contain: the whole picture, centred
    const fit = Math.min(width / iw, boxHeight / ih);
    const w = iw * fit;
    const h = ih * fit;
    ctx.drawImage(image, left + (width - w) / 2, top + (boxHeight - h) / 2, w, h);
  }
}

function drawStatusBar(ctx, left, top, width) {
  ctx.fillStyle = '#fff';
  ctx.font = `600 ${pt(17)}px ${FONT}`;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillText('22:00', left + pt(56), top + pt(30));
  // Dynamic Island
  ctx.fillStyle = '#000';
  roundRect(ctx, left + (width - pt(126)) / 2, top + pt(11), pt(126), pt(37), pt(18.5));
  ctx.fill();
  // signal, wifi, battery
  const right = left + width - pt(28);
  const mid = top + pt(30);
  ctx.fillStyle = '#fff';
  for (let i = 0; i < 4; i += 1) {
    const h = pt(4 + i * 2.2);
    ctx.fillRect(right - pt(72) + i * pt(4.6), mid + pt(5) - h, pt(3), h);
  }
  ctx.strokeStyle = '#fff';
  ctx.lineWidth = pt(1.8);
  ctx.lineCap = 'round';
  for (let i = 0; i < 3; i += 1) {
    ctx.beginPath();
    ctx.arc(right - pt(41), mid + pt(5), pt(3 + i * 3.2), Math.PI * 1.25, Math.PI * 1.75);
    ctx.stroke();
  }
  ctx.lineWidth = pt(1);
  roundRect(ctx, right - pt(26), mid - pt(6), pt(25), pt(12), pt(3.5));
  ctx.strokeStyle = 'rgba(255,255,255,0.5)';
  ctx.stroke();
  ctx.fillStyle = '#fff';
  roundRect(ctx, right - pt(24), mid - pt(4), pt(19), pt(8), pt(2));
  ctx.fill();
  ctx.fillStyle = 'rgba(255,255,255,0.5)';
  ctx.fillRect(right - pt(0.5), mid - pt(2), pt(1.6), pt(4));
}

function drawCloseButton(ctx, left, top, width) {
  const cx = left + width - pt(12 + 22);
  const cy = top + pt(INSET_TOP + 2 + 22);
  ctx.fillStyle = 'rgba(0,0,0,0.55)';
  ctx.beginPath();
  ctx.arc(cx, cy, pt(22), 0, Math.PI * 2);
  ctx.fill();
  ctx.strokeStyle = '#EDEDED';
  ctx.lineWidth = pt(2.4);
  ctx.lineCap = 'round';
  const r = pt(7.5);
  ctx.beginPath();
  ctx.moveTo(cx - r, cy - r); ctx.lineTo(cx + r, cy + r);
  ctx.moveTo(cx + r, cy - r); ctx.lineTo(cx - r, cy + r);
  ctx.stroke();
}

function drawHostPill(ctx, left, top, width, handle, avatar) {
  if (!handle) return;
  const name = String(handle).replace(/^@/, '');
  const height = pt(44);
  const rightEdge = left + width - pt(12 + 44 + 8);
  const y = top + pt(INSET_TOP + 2);
  ctx.font = `600 ${pt(14)}px ${FONT}`;
  const textMax = pt(210 - 7 - 30 - 9 - 2 - 18 - 8);
  const label = ellipsize(ctx, `@${name}`, textMax);
  const pillWidth = pt(7 + 30 + 9) + ctx.measureText(label).width + pt(2 + 18 + 8);
  const x = rightEdge - pillWidth;
  ctx.fillStyle = 'rgba(0,0,0,0.55)';
  roundRect(ctx, x, y, pillWidth, height, height / 2);
  ctx.fill();
  // the avatar: the account's profile picture, or its initial on the follow gradient
  // (the app's look for an account whose picture it cannot load)
  const ax = x + pt(7 + 15);
  const ay = y + height / 2;
  if (avatar) {
    ctx.save();
    ctx.beginPath();
    ctx.arc(ax, ay, pt(15), 0, Math.PI * 2);
    ctx.clip();
    const side = Math.min(avatar.width, avatar.height);
    ctx.drawImage(avatar, (avatar.width - side) / 2, (avatar.height - side) / 2, side, side,
      ax - pt(15), ay - pt(15), pt(30), pt(30));
    ctx.restore();
  } else {
    const gradient = ctx.createLinearGradient(ax - pt(15), ay + pt(15), ax + pt(15), ay - pt(15));
    FOLLOW_GRADIENT.forEach((color, i) => gradient.addColorStop(i / (FOLLOW_GRADIENT.length - 1), color));
    ctx.fillStyle = gradient;
    ctx.beginPath();
    ctx.arc(ax, ay, pt(15), 0, Math.PI * 2);
    ctx.fill();
    ctx.fillStyle = '#fff';
    ctx.font = `800 ${pt(13)}px ${FONT}`;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText(name.charAt(0).toUpperCase(), ax, ay + pt(0.5));
  }
  ctx.fillStyle = '#F4F4F5';
  ctx.font = `600 ${pt(14)}px ${FONT}`;
  ctx.textAlign = 'left';
  const tx = x + pt(7 + 30 + 9);
  ctx.fillText(label, tx, ay);
  // chevron
  const cx = tx + ctx.measureText(label).width + pt(2 + 9);
  ctx.strokeStyle = 'rgba(255,255,255,0.6)';
  ctx.lineWidth = pt(2);
  ctx.lineCap = 'round';
  ctx.lineJoin = 'round';
  ctx.beginPath();
  ctx.moveTo(cx - pt(2), ay - pt(4.5));
  ctx.lineTo(cx + pt(2.5), ay);
  ctx.lineTo(cx - pt(2), ay + pt(4.5));
  ctx.stroke();
}

function drawPin(ctx, cx, cy) {
  ctx.fillStyle = '#FF3B30';
  ctx.beginPath();
  ctx.arc(cx, cy, pt(9), 0, Math.PI * 2);
  ctx.fill();
  ctx.fillStyle = '#fff';
  ctx.beginPath();
  ctx.arc(cx, cy - pt(1.4), pt(3.6), Math.PI * 0.85, Math.PI * 2.15);
  ctx.lineTo(cx, cy + pt(4.6));
  ctx.closePath();
  ctx.fill();
  ctx.fillStyle = '#FF3B30';
  ctx.beginPath();
  ctx.arc(cx, cy - pt(1.4), pt(1.3), 0, Math.PI * 2);
  ctx.fill();
}

function drawOpenIcon(ctx, x, cy) {
  const s = pt(9);
  ctx.strokeStyle = 'rgba(255,255,255,0.4)';
  ctx.lineWidth = pt(1.2);
  ctx.lineCap = 'round';
  ctx.beginPath();
  ctx.moveTo(x + s * 0.45, cy - s / 2);
  ctx.lineTo(x, cy - s / 2);
  ctx.lineTo(x, cy + s / 2);
  ctx.lineTo(x + s, cy + s / 2);
  ctx.lineTo(x + s, cy + s * 0.05);
  ctx.moveTo(x + s * 0.6, cy - s / 2);
  ctx.lineTo(x + s, cy - s / 2);
  ctx.lineTo(x + s, cy - s * 0.1);
  ctx.moveTo(x + s, cy - s / 2);
  ctx.lineTo(x + s * 0.45, cy + s * 0.05);
  ctx.stroke();
}

function drawHeroText(ctx, left, heroBottom, width, { title, type, address }) {
  const x = left + pt(16);
  const rowWidth = width - pt(32);
  const rowBottom = heroBottom - pt(12 + HERO_BUMP_UP);
  const rowHeight = pt(30);
  const rowMid = rowBottom - rowHeight / 2;

  // the badge: yellow "Event", blue "Club"
  const isClub = type === 'Club';
  ctx.font = `800 ${pt(10)}px ${FONT}`;
  const badgeText = isClub ? 'Club' : 'Event';
  const badgeW = ctx.measureText(badgeText).width + pt(12);
  const badgeH = pt(16);
  ctx.fillStyle = isClub ? '#1E90FF' : '#FFC107';
  roundRect(ctx, x, rowMid - badgeH / 2, badgeW, badgeH, pt(2));
  ctx.fill();
  ctx.fillStyle = isClub ? '#FFFFFF' : '#1A1A1A';
  ctx.textAlign = 'left';
  ctx.textBaseline = 'middle';
  ctx.fillText(badgeText, x + pt(6), rowMid + pt(0.5));

  // the address pill, at the right end of the row
  const label = locationLabel(address);
  if (label) {
    ctx.font = `500 ${pt(11)}px ${FONT}`;
    const fixed = pt(8 + 18 + 6 + 5 + 9 + 8);
    const maxPill = rowWidth - badgeW - pt(10);
    const text = ellipsize(ctx, label, Math.max(pt(20), maxPill - fixed));
    const pillW = Math.min(maxPill, fixed + ctx.measureText(text).width);
    const px = x + rowWidth - pillW;
    ctx.fillStyle = 'rgba(0,0,0,0.52)';
    roundRect(ctx, px, rowMid - rowHeight / 2, pillW, rowHeight, pt(20));
    ctx.fill();
    ctx.strokeStyle = 'rgba(255,255,255,0.12)';
    ctx.lineWidth = pt(1);
    ctx.stroke();
    drawPin(ctx, px + pt(8 + 9), rowMid);
    ctx.fillStyle = 'rgba(255,255,255,0.90)';
    ctx.fillText(text, px + pt(8 + 18 + 6), rowMid + pt(0.5));
    drawOpenIcon(ctx, px + pt(8 + 18 + 6 + 5) + ctx.measureText(text).width, rowMid);
  }

  // the title: one line, made smaller until it fits (adjustsFontSizeToFit)
  let size = 25;
  const titleText = String(title || 'Titel');
  ctx.font = `800 ${pt(size)}px ${FONT}`;
  while (size > 12 && ctx.measureText(titleText).width > rowWidth) {
    size -= 0.5;
    ctx.font = `800 ${pt(size)}px ${FONT}`;
  }
  ctx.fillStyle = '#fff';
  ctx.textBaseline = 'alphabetic';
  ctx.fillText(ellipsize(ctx, titleText, rowWidth), x, rowBottom - rowHeight - pt(4) - pt(size * 0.3));
}

function drawIcon(ctx, kind, cx, cy, color) {
  ctx.strokeStyle = color;
  ctx.fillStyle = color;
  ctx.lineWidth = pt(1.9);
  ctx.lineCap = 'round';
  const s = pt(9.5);
  if (kind === 'music') {
    ctx.beginPath();
    ctx.arc(cx, cy + s * 0.15, s * 0.82, Math.PI, 0);
    ctx.stroke();
    roundRect(ctx, cx - s * 0.95, cy + s * 0.05, s * 0.5, s * 0.8, s * 0.15); ctx.fill();
    roundRect(ctx, cx + s * 0.45, cy + s * 0.05, s * 0.5, s * 0.8, s * 0.15); ctx.fill();
  } else if (kind === 'info') {
    const g = s * 0.12;
    roundRect(ctx, cx - s * 0.9, cy - s * 0.9, s * 0.9 - g, s * 1.1, s * 0.1); ctx.fill();
    roundRect(ctx, cx + g, cy - s * 0.9, s * 0.9 - g, s * 0.6, s * 0.1); ctx.fill();
    roundRect(ctx, cx - s * 0.9, cy + s * 0.2 + g, s * 0.9 - g, s * 0.7 - g, s * 0.1); ctx.fill();
    roundRect(ctx, cx + g, cy - s * 0.3 + g, s * 0.9 - g, s * 1.2 - g, s * 0.1); ctx.fill();
  } else {
    [[-0.7, 0.4], [-0.15, 1.1], [0.4, 0.75]].forEach(([dx, h]) => {
      ctx.fillRect(cx + dx * s, cy + s * 0.9 - h * s * 1.6, s * 0.36, h * s * 1.6);
    });
  }
}

function drawSheet(ctx, left, heroBottom, width, screenBottom) {
  // the backplate under the sheet, and the sheet with its rounded top over the picture
  ctx.fillStyle = '#08080A';
  ctx.fillRect(left, heroBottom + pt(18), width, screenBottom - heroBottom);
  const sx = left + pt(16);
  const sw = width - pt(32);
  const sy = heroBottom - pt(CARD_OVERLAP);
  roundRect(ctx, sx, sy, sw, screenBottom - sy, { tl: pt(18), tr: pt(18), br: 0, bl: 0 });
  ctx.fill();
  // the tab bar: Musik is open, as when the card opens
  ctx.fillStyle = 'rgba(0,0,0,0.35)';
  roundRect(ctx, sx, sy, sw, pt(TAB_BAR_PT), { tl: pt(18), tr: pt(18), br: 0, bl: 0 });
  ctx.fill();
  ctx.fillStyle = 'rgba(255,255,255,0.08)';
  ctx.fillRect(sx, sy + pt(TAB_BAR_PT) - 1, sw, Math.max(1, pt(1)));
  const tabW = (sw - pt(8)) / 3;
  ctx.fillStyle = 'rgba(255,255,255,0.10)';
  roundRect(ctx, sx + pt(4), sy + pt(4), tabW, pt(TAB_BAR_PT - 8), pt(TAB_BAR_PT - 8) / 2);
  ctx.fill();
  [['music', 'Musik'], ['info', 'Details'], ['stats', 'Statistik']].forEach(([kind, label], i) => {
    const color = i === 0 ? '#FFFFFF' : '#555E6E';
    const cx = sx + pt(4) + tabW * (i + 0.5);
    drawIcon(ctx, kind, cx, sy + pt(4 + 9 + 9.5), color);
    ctx.fillStyle = color;
    ctx.font = `600 ${pt(10)}px ${FONT}`;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText(label, cx, sy + pt(4 + 9 + 19 + 4 + 6.5));
  });
}

/**
 * Draws the preview: {@code image} is the picture (an ImageBitmap or a loaded <img>),
 * {@code avatar} the host's profile picture or null, {@code logo} Schick's white logo.
 * Returns the canvas.
 */
export function drawAppPreview(canvas, { image, avatar, logo, title, type, address, handle }) {
  const { boxAspect, tall } = heroBox(image.width, image.height);
  const height = previewHeight(boxAspect);
  canvas.width = CANVAS_WIDTH;
  canvas.height = height;
  const ctx = canvas.getContext('2d');

  drawBackground(ctx, height);
  drawLogo(ctx, logo);
  drawPhoneBody(ctx, height);

  const left = (CANVAS_WIDTH - SCREEN_WIDTH) / 2;
  const top = PHONE_TOP + PHONE_BEZEL;
  const screenBottom = height + 10;
  const heroHeight = SCREEN_WIDTH * boxAspect;
  ctx.save();
  roundRect(ctx, left, top, SCREEN_WIDTH, screenBottom - top + 200, { tl: 100, tr: 100, br: 0, bl: 0 });
  ctx.clip();
  ctx.fillStyle = '#08080A';
  ctx.fillRect(left, top, SCREEN_WIDTH, screenBottom - top);
  drawPicture(ctx, image, left, top, SCREEN_WIDTH, heroHeight, tall);
  const fadeHeight = pt(Math.min(200, Math.round(SCREEN_PT * boxAspect * 0.36)));
  const fade = ctx.createLinearGradient(0, top + heroHeight, 0, top + heroHeight - fadeHeight);
  fade.addColorStop(0, 'rgba(0,0,0,0.6)');
  fade.addColorStop(1, 'rgba(0,0,0,0)');
  ctx.fillStyle = fade;
  ctx.fillRect(left, top + heroHeight - fadeHeight, SCREEN_WIDTH, fadeHeight);
  drawStatusBar(ctx, left, top, SCREEN_WIDTH);
  drawHostPill(ctx, left, top, SCREEN_WIDTH, handle, avatar);
  drawCloseButton(ctx, left, top, SCREEN_WIDTH);
  drawHeroText(ctx, left, top + heroHeight, SCREEN_WIDTH, { title, type, address });
  drawSheet(ctx, left, top + heroHeight, SCREEN_WIDTH, screenBottom);
  ctx.restore();
  return canvas;
}

/** A blob as something drawImage takes. */
export async function decodeImage(blob) {
  if (typeof createImageBitmap === 'function') {
    try {
      return await createImageBitmap(blob);
    } catch {
      // some formats only an <img> decodes
    }
  }
  const url = URL.createObjectURL(blob);
  try {
    return await new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => resolve(img);
      img.onerror = () => reject(new Error('Bild nicht lesbar'));
      img.src = url;
    });
  } finally {
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  }
}

/** Loads an image file of our own (the logo). */
export const loadImage = (src) => new Promise((resolve) => {
  const img = new Image();
  img.onload = () => resolve(img);
  img.onerror = () => resolve(null);
  img.src = src;
});

/** The preview as a PNG blob; without an avatar the host pill shows the initial. */
export async function renderAppPreview({ imageBlob, avatarBlob, logoSrc, ...card }) {
  const [image, avatar, logo] = await Promise.all([
    decodeImage(imageBlob),
    avatarBlob ? decodeImage(avatarBlob).catch(() => null) : null,
    logoSrc ? loadImage(logoSrc) : null,
  ]);
  const canvas = drawAppPreview(document.createElement('canvas'), { image, avatar, logo, ...card });
  return new Promise((resolve, reject) => canvas.toBlob(
    (blob) => (blob ? resolve(blob) : reject(new Error('Kein Bild erzeugt'))), 'image/png'));
}
